<template>
  <v-container>
    <v-snackbar v-model="snackbar" class="elevation-24" :color="snackbarColor">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn icon="mdi-close" @click="snackbar = false"> </v-btn>
      </template>
    </v-snackbar>

    <!-- Invitation Form -->
    <v-row>
      <v-col cols="12" sm="6" md="4">
        <v-card class="pa-3" variant="outlined">
          <v-card-title>Invite Person</v-card-title>
          <v-form ref="form" v-model="valid">
            <!-- Name Input -->
            <v-text-field
              v-model="invitation.name"
              label="Name"
              :rules="nameRules"
              required
            />

            <!-- Email Input -->
            <v-text-field
              v-model="invitation.email"
              label="Email"
              :rules="emailRules"
              required
            />

            <!-- Event Selection -->
            <v-select
              v-model="invitation.gender"
              :items="gender"
              label="Select gender"
              required
            />

            <!-- Age Input -->
            <v-text-field v-model="invitation.age" label="Age" />

            <!-- Event Selection -->
            <v-select
              v-model="invitation.event_id"
              :items="events"
              item-value="id"
              item-title="name"
              label="Select Event"
              required
            />

            <v-btn
              @click="sendInvitation"
              prepend-icon="mdi-plus"
              color="grey-darken-3"
              block
              >ADD</v-btn
            >
          </v-form>
        </v-card>
      </v-col>

      <!-- Invitations List -->
      <v-col cols="12" sm="6" md="8">
        <v-card border="opacity-50 sm" class="pa-3" variant="outlined">
          <template v-slot:text>
            <v-text-field
              v-model="search"
              label="Search"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              hide-details
              single-line
            ></v-text-field>
          </template>
          <v-card-title>Invitations List</v-card-title>
          <v-data-table
            :headers="headers"
            :items="invitations"
            item-key="id"
            :items-per-page="5"
            :search="search"
          >
            <!-- checkbox check in  -->
            <template v-slot:[`item.checked_in`]="{ item }">
              <v-checkbox
                v-model="item.checked_in"
                :true-value="1"
                :false-value="0"
                color="success"
                readonly
              ></v-checkbox>
            </template>
            <template v-slot:[`item.qr_code`]="{ item }">
              <!-- Display QR Code if available -->
              <v-img
                :src="item.qr_code"
                min-width="100"
                min-height="100"
                v-if="item.qr_code"
                @click="downloadImage(item.qr_code)"
                style="cursor: pointer"
                class="ma-1"
              ></v-img>
              <span v-else>No QR Code</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <div class="d-flex justify-space-between gap-2" style="gap: 2px">
                <!-- Generate QR Code for Invitation -->
                <v-btn
                  @click="generateQrCode(item)"
                  icon="mdi-qrcode-scan"
                  color="grey-darken-3"
                  size="small"
                >
                </v-btn>
                <!-- Delete Invitation -->
                <v-btn
                  @click="deleteInvitation(item.id)"
                  icon="mdi-delete"
                  color="grey-darken-3"
                  size="small"
                >
                </v-btn>
                <v-btn
                  @click="openEditDialog(item)"
                  icon="mdi-pencil"
                  color="grey-darken-3"
                  size="small"
                ></v-btn>
                <v-btn
                  @click="downloadOrShareInvitation(item, false)"
                  icon="mdi-download"
                  title="Download invitation"
                  color="grey-darken-3"
                  size="small"
                >
                </v-btn>
                <v-btn
                  @click="downloadOrShareInvitation(item, true)"
                  icon="mdi-share"
                  title="Download invitation"
                  color="grey-darken-3"
                  size="small"
                >
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="500px">
      <v-card>
        <v-card-title>Edit Attendee</v-card-title>
        <v-card-text>
          <v-text-field v-model="editedAttendee.name" label="Name" />
          <v-text-field v-model="editedAttendee.email" label="Email" />
          <!-- Event Selection -->
          <v-select
            v-model="editedAttendee.gender"
            :items="gender"
            label="Select gender"
            required
          />

          <v-checkbox
            v-model="editedAttendee.checked_in"
            :true-value="1"
            :false-value="0"
            color="success"
            label="Check-in"
          />
        </v-card-text>
        <!-- Event Selection -->
        <v-select
          v-model="editedAttendee.event_id"
          :items="events"
          item-value="id"
          item-title="name"
          label="Select Event"
          required
        />
        <v-card-actions>
          <v-btn color="primary" @click="updateAttendee">Save</v-btn>
          <v-btn text @click="editDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";

export default {
  data() {
    return {
      editDialog: false,
      editedAttendee: {
        id: "",
        name: "",
        email: "",
        checked_in: false,
        checkin_time: "",
        event_id: "",
        gender: "",
      },
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      valid: false,
      dialog: false,
      search: "",
      gender: ["Male", "Female"],
      invitation: {
        name: "",
        email: "",
        event_id: "",
        qrCodeUrl: "",
        age: 0,
        checked_in: false,
        event_name: "",
        gender: "",
      },
      invitations: [],
      events: [], // List of events fetched from the API
      nameRules: [(v) => !!v || "Name is required"],
      emailRules: [
        (v) => !!v || "Email is required",
        (v) => /.+@.+\..+/.test(v) || "Email must be valid",
      ],
      headers: [
        { title: "Name", value: "name", sortable: true },
        { title: "Email", value: "email" },
        { title: "Event", value: "event_name" },
        { title: "Checked in", value: "checked_in" },
        { title: "QrCode", value: "qr_code" },
        { title: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  mounted() {
    this.fetchEvents();
    this.fetchInvitations();
  },
  methods: {
    async downloadOrShareInvitation(invite, share) {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // === QR Code Box Setup ===
      const qrBoxWidth = 70;
      const qrBoxHeight = 80;
      const qrBoxX = (pageWidth - qrBoxWidth) / 2;
      const qrBoxY = 140;
      const qrSize = 50;
      const qrX = qrBoxX + (qrBoxWidth - qrSize) / 2;
      const qrY = qrBoxY + 10;

      const qrCodeDataUrl = await QRCode.toDataURL(
        invite.event_id + "*" + invite.name + "*" + invite.email
      );

      // === Watermark (centered, rotated, light) ===
      doc.saveGraphicsState();
      doc.setTextColor(230, 230, 230);
      doc.setFontSize(50);
      doc.setFont("helvetica", "bold");
      doc.text("Sunrise Events", pageWidth / 2, pageHeight / 2 + 50, {
        align: "center",
        angle: 45,
      });
      doc.restoreGraphicsState();

      // === Shadow Border ===
      // doc.setDrawColor("#cccccc");
      // doc.setLineWidth(0.5);
      // doc.rect(11, 11, pageWidth - 22, pageHeight - 22);

      // === Dashed Main Border ===
      doc.setDrawColor("#000000");
      doc.setLineDashPattern([4, 2], 0);
      doc.setLineWidth(0.8);
      doc.rect(10, 10, pageWidth - 20, pageHeight - 20);
      doc.setLineDashPattern([], 0);

      // === Header ===
      doc.setFillColor("#000000");
      doc.rect(10, 10, pageWidth - 20, 30, "F");
      doc.setTextColor("#FFFFFF");
      doc.setFontSize(18);

      doc.addImage(
        "https://sunrise-event.com/uploads/" + invite.event_image,
        "PNG",
        15,
        12,
        qrSize / 2,
        qrSize / 2
      );

      if (invite.event_image2 != null) {
        doc.addImage(
          "https://sunrise-event.com/uploads/" + invite.event_image2,
          "PNG",
          pageWidth - 15 - qrSize / 2,
          12,
          qrSize / 2,
          qrSize / 2
        );
      }

      doc.text(`${invite.event_name} — You're Invited!`, pageWidth / 2, 28, {
        align: "center",
      });

      // === Invitee Info ===
      doc.setFontSize(14);
      doc.setTextColor("#000000");
      doc.text(`Name: ${invite.name}`, 20, 55);
      doc.text(`Email: ${invite.email}`, 20, 65);
      doc.text(`Event ID: ${invite.event_id}`, 20, 75);

      // === Invitation Message ===
      doc.setFontSize(12);
      doc.setTextColor("#333333");
      //       const message = `You're cordially invited to join us for an unforgettable experience at our upcoming event.
      // We look forward to seeing you there!`;
      const message = invite.description;
      doc.text(message, 20, 90, { maxWidth: pageWidth - 40 });

      // Draw QR box border
      doc.setDrawColor("#888888");
      doc.setLineWidth(0.5);
      doc.rect(qrBoxX, qrBoxY, qrBoxWidth, qrBoxHeight);

      // QR code inside box (centered within)
      doc.addImage(qrCodeDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

      // Text below QR code inside the box
      doc.setFontSize(11);
      doc.setTextColor("#333333");
      doc.text(
        "Present this code at the entrance for fast check-in.",
        pageWidth / 2,
        qrBoxY + qrBoxHeight + 30,
        { align: "center" }
      );

      doc.setFillColor("#000000");
      doc.rect(10, pageHeight - 30, pageWidth - 20, 20, "F");
      doc.setTextColor("#FFFFFF");
      doc.setFontSize(10);
      doc.text("Sunrise Events · © 2025", pageWidth / 2, pageHeight - 17, {
        align: "center",
      });

      // === Save PDF ===
      if (!share) {
        doc.save(`${invite.event_name}-${invite.name}.pdf`);
      } else {
        const pdfBase64 = doc.output("datauristring"); // Base64 with prefix
        const base64Content = pdfBase64.split(",")[1];

        axios
          .post("/api/send-invitation-email", {
            pdfBase64: base64Content,
            fileName: `${invite.event_name}-${invite.name}.pdf`,
            email: invite.email,
            name: invite.name,
            event_name: invite.event_name,
          })
          .then((resp) => {
            this.snackbarText = resp.data.message;
            this.snackbar = true;
            this.snackbarColor = "success";
          })
          .catch((e) => {
            console.log(e);

            this.snackbarText = "Invitation could not be sent!";
            this.snackbar = true;
            this.snackbarColor = "red";
          });
      }
    },
    downloadImage(qr_code_url) {
      const link = document.createElement("a");
      link.href = qr_code_url; // Use the QR code URL from the invitation
      console.log("Downloading image from URL:", qr_code_url);

      // Optional: set a default filename
      link.download = "downloaded-image.jpg";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    openEditDialog(item) {
      this.editedAttendee = { ...item };
      this.editDialog = true;
      console.log("Edit Dialog Opened", this.editedAttendee);
    },
    async updateAttendee() {
      try {
        var editedAttendee = this.editedAttendee;
        await axios.put(`/api/attendees/${editedAttendee.id}`, editedAttendee);
        this.editDialog = false;
        this.snackbarText = "Attendee updated successfully!";
        this.snackbar = true;
        this.snackbarColor = "success";
        await this.fetchInvitations();
      } catch (err) {
        this.snackbarText = "Error updating attendee!";
        this.snackbar = true;
        this.snackbarColor = "red";
        this.editDialog = false;
        console.error("Update failed:", err);
      }
    },
    // Fetch events from API (you can modify this to match your backend)
    async fetchEvents() {
      try {
        const response = await axios.get("/api/events");
        this.events = response.data;
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    },

    // Fetch invitations from API
    async fetchInvitations() {
      try {
        const response = await axios.get("/api/attendees");
        this.invitations = response.data.map((inv) => ({
          ...inv,
          eventName: this.getEventNameById(inv.event_id), // Display event name in the table
          event_image: this.getEventImageById(inv.event_id), // Display event name in the table
          event_image2: this.getEventImageById2(inv.event_id), // Display event name in the table
        }));
      } catch (error) {
        console.error("Error fetching attendees:", error);
      }
    },

    // Get event name by event ID (simplified, assumes all events are fetched)
    getEventNameById(event_id) {
      const event = this.events.find((e) => e.id == event_id);
      return event ? event.name : "Unknown Event";
    },
    // Get event name by event ID (simplified, assumes all events are fetched)
    getEventImageById(event_id) {
      const event = this.events.find((e) => e.id == event_id);
      console.log("getEventImageById event", event);

      return event ? event.image : "Unknown Event";
    },
    getEventImageById2(event_id) {
      const event = this.events.find((e) => e.id == event_id);
      console.log("getEventImageById event", event);

      return event ? event.image2 : "Unknown Event";
    },
    getEventDescriptionById(event_id) {
      const event = this.events.find((e) => e.id == event_id);
      console.log("getEventImageById event", event);

      return event ? event.description : "Unknown Event";
    },

    // Send the invitation (post to the API)
    async sendInvitation() {
      console.log("Sending invitation:", this.invitation);

      if (this.$refs.form.validate()) {
        try {
          await axios
            .post("/api/register", this.invitation)
            .then((response) => {
              console.log("Invitation sent:", response.data);
              this.fetchInvitations(); // Refresh the invitations list
              this.dialog = false; // Close the dialog
              this.snackbarText = "Invitation sent successfully!";
              this.snackbar = true;
              this.snackbarColor = "success";
              this.invitation.event_image = this.getEventImageById(
                this.invitation.event_id
              );
              this.invitation.event_image2 = this.getEventImageById2(
                this.invitation.event_id
              );
              this.invitation.event_name = this.getEventNameById(
                this.invitation.event_id
              );
              this.invitation.description = this.getEventDescriptionById(
                this.invitation.event_id
              );
              console.log("lolooooooooo", this.invitation);

              this.downloadOrShareInvitation(this.invitation, true);
            })
            .catch((error) => {
              console.log(
                "******Error sending invitation:",
                error.response.data.error
              );
              this.snackbarText =
                "Error sending invitation! " + error.response.data.error;
              this.snackbar = true;
              this.snackbarColor = "red";
            });
        } catch (error) {
          console.error("Error sending attendees:", error.data);
        }
      }
    },

    // Generate a QR code for the invitation
    async generateQrCode(invitation) {
      try {
        const qrCodeUrl = await QRCode.toDataURL(
          `${invitation.event_id}*${invitation.name}*${invitation.email}`
        );
        await axios.patch(`/api/attendees/${invitation.id}`, {
          qrCodeUrl: qrCodeUrl,
        });
        this.fetchInvitations(); // Refresh the invitations list

        // You can do whatever you'd like with the QR code URL (show it in a dialog or download it)
        alert(`QR Code Generated for Invitation ID: ${invitation.id}`);
        console.log(qrCodeUrl); // Here you can use the QR code URL as needed (e.g., display the image, save it, etc.)
      } catch (error) {
        console.error("Error generating QR code:", error);
      }
    },

    // Delete an invitation
    async deleteInvitation(invitationId) {
      if (!confirm("Are you sure you want to delete this attendee?")) {
        return;
      }

      try {
        axios
          .delete(`/api/attendees/delete/${invitationId}`, {
            attId: invitationId,
          })
          .then(() => {
            this.snackbarText = "Invitation deleted successfully!";
            this.snackbar = true;
            this.snackbarColor = "success";
            this.fetchInvitations(); // Refresh the invitations list
            console.log("Invitation deleted:", invitationId);
          })
          .catch((error) => {
            console.log("******Error deleting invitation:", error);
          });
        this.fetchInvitations(); // Refresh the invitations list after deletion
      } catch (error) {
        console.error("Error deleting attendees:", error);
      }
    },
  },
};
</script>

<style scoped>
/* Add any styles specific to this page */
</style>
